import React from 'react';

// Form primitives for the admission flow. Each one owns its label, its error
// message and the aria wiring between them, so no step has to remember it.

const describedBy = (id, error, hint) => {
  const ids = [];
  if (hint) ids.push(`${id}-hint`);
  if (error) ids.push(`${id}-error`);
  return ids.length > 0 ? ids.join(' ') : undefined;
};

/** Label, control, hint and error in the layout every field shares. */
export const Field = ({ id, label, error, hint, optional = false, children }) => (
  <div className={`ad-field ${error ? 'has-error' : ''}`}>
    <label className="ad-field-label" htmlFor={id}>
      {label}
      {optional && <span className="ad-optional"> (optional)</span>}
    </label>
    {hint && (
      <p className="ad-field-hint" id={`${id}-hint`}>
        {hint}
      </p>
    )}
    {children}
    {/*
      No role="alert" here: the message is wired to the control through
      aria-describedby and announced when focus reaches it. The step's error
      summary is the single live region, so nothing is read out twice.
    */}
    {error && (
      <p className="ad-field-error" id={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

export const TextField = ({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  optional,
  type = 'text',
  ...rest
}) => (
  <Field id={id} label={label} error={error} hint={hint} optional={optional}>
    <input
      className="ad-input"
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedBy(id, error, hint)}
      {...rest}
    />
  </Field>
);

export const TextAreaField = ({ id, label, value, onChange, error, hint, optional, rows = 4 }) => (
  <Field id={id} label={label} error={error} hint={hint} optional={optional}>
    <textarea
      className="ad-input ad-textarea"
      id={id}
      name={id}
      rows={rows}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedBy(id, error, hint)}
    />
  </Field>
);

export const SelectField = ({
  id,
  label,
  value,
  onChange,
  options,
  error,
  hint,
  optional,
  placeholder = 'Please choose',
}) => (
  <Field id={id} label={label} error={error} hint={hint} optional={optional}>
    <div className="ad-select-wrap">
      <select
        className="ad-input ad-select"
        id={id}
        name={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(id, error, hint)}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="ad-select-arrow" aria-hidden="true" />
    </div>
  </Field>
);

/** Two fields side by side on wide screens, stacked on small ones. */
export const FieldRow = ({ children }) => <div className="ad-field-row">{children}</div>;

/** A titled group of fields within a step. */
export const FieldSet = ({ title, description, children }) => (
  <fieldset className="ad-fieldset">
    <legend className="ad-legend">{title}</legend>
    {description && <p className="ad-fieldset-note">{description}</p>}
    {children}
  </fieldset>
);
